import {
    Alert,
    Button,
    Card,
    Modal,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
    Tooltip,
    Divider
} from "antd";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    FileExcelOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    UploadOutlined
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

const INTERMEDIAIRE_TEMPLATE_HEADERS = [
    "CodeIntermediaire",
    "Nom",
    "Prenom",
    "Genre",
    "Contact",
    "Adresse",
    "Email",
    "TypePersonne",
    "TypeIntermediaire",
    "Actif",
];

const GENRE_VALUES = ["MASCULIN", "FEMININ", "AUTRE"];
const TYPE_PERSONNE_VALUES = ["PHYSIQUE", "MORALE"];
const TYPE_INTERMEDIAIRE_VALUES = ["COURTIER", "AGENT_GENERAL", "APPORTEUR_REGULIER", "APPORTEUR_OCCASIONNEL"];
const ACTIF_VALUES = [ "oui", "non"];

const SAMPLE_ROW = [
    "001",
    "Doe",
    "John",
    "MASCULIN",
    "90112233",
    "123 Rue Exemple",
    "poolins.oss@example.com",
    "PHYSIQUE",
    "APPORTEUR_REGULIER",
    "oui",
];

const ImportIntermediairesModal = ({ open, onClose, colors, onImported }) => {
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importConfirm, setImportConfirm] = useState(null);
    const [importError, setImportError] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const importRows = useMemo(() => importPreview?.rows || [], [importPreview]);

    const validRowsCount = useMemo(() => {
        if (!importPreview?.rows) return 0;
        if (typeof importPreview.validRows === "number") {
            return importPreview.validRows;
        }
        return importPreview.rows.filter((row) => row.valid).length;
    }, [importPreview]);

    const invalidRowsCount = useMemo(() => {
        if (!importPreview?.rows) return 0;
        if (typeof importPreview.invalidRows === "number") {
            return importPreview.invalidRows;
        }
        return importPreview.rows.filter((row) => !row.valid).length;
    }, [importPreview]);

    const importSummary = useMemo(() => {
        if (!importPreview) return null;
        const total = importPreview?.totalRows ?? importRows.length ?? 0;
        const valid = importPreview?.validRows ?? validRowsCount;
        const invalid = importPreview?.invalidRows ?? invalidRowsCount;
        return { total, valid, invalid };
    }, [importPreview, importRows, validRowsCount, invalidRowsCount]);

    const confirmSummary = useMemo(() => {
        if (!importConfirm) return null;
        return {
            total: importConfirm?.totalRows ?? importRows.length ?? 0,
            created: importConfirm?.createdRows ?? 0,
            failed: importConfirm?.failedRows ?? 0,
        };
    }, [importConfirm, importRows]);

    useEffect(() => {
        if (open) {
            resetImportState();
        }
    }, [open]);

    const isExcelFile = (file) => {
        if (!file) return false;
        return /\.xlsx?$/.test((file.name || "").toLowerCase());
    };

    const resetImportState = () => {
        setImportFile(null);
        setImportPreview(null);
        setImportConfirm(null);
        setImportError(null);
        setPreviewLoading(false);
        setConfirmLoading(false);
    };

    const handleClose = () => {
        resetImportState();
        onClose?.();
    };

    const handleFileSelect = (file) => {
        if (!isExcelFile(file)) {
            toast.error("Format de fichier invalide. Choisissez un fichier .xlsx ou .xls");
            return Upload.LIST_IGNORE;
        }
        setImportFile(file);
        setImportPreview(null);
        setImportConfirm(null);
        setImportError(null);
        return false;
    };

    const handlePreviewImport = async () => {
        if (!importFile) {
            toast.error("Ajoutez d'abord un fichier Excel");
            return;
        }
        setPreviewLoading(true);
        setImportError(null);
        setImportConfirm(null);
        const formData = new FormData();
        formData.append("file", importFile);
        try {
            const res = await axiosInstance.post("/api/intermediaire/import/preview", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportPreview(res.data);
            toast.success("Prévisualisation effectuée");
        } catch (error) {
            console.log(error);
            const message =
                error?.response?.data?.message ||
                "Erreur lors de la prévisualisation. Vérifiez les en-têtes ou le contenu du fichier.";
            setImportError(message);
            toast.error(message);
        }
        setPreviewLoading(false);
    };

    const handleConfirmImport = async () => {
        if (!importFile || !importPreview || validRowsCount === 0) {
            toast.error("Ajoutez un fichier et assurez-vous d'avoir au moins une ligne valide");
            return;
        }
        setConfirmLoading(true);
        setImportError(null);
        const formData = new FormData();
        formData.append("file", importFile);
        try {
            const res = await axiosInstance.post("/api/intermediaire/import/confirm", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportConfirm(res.data);
            setImportPreview(res.data);
            toast.success("Import confirmé");
            onImported?.();
            handleClose();
        } catch (error) {
            console.log(error);
            const message =
                error?.response?.data?.message ||
                "Erreur lors de la confirmation. Veuillez vérifier le fichier puis réessayer.";
            setImportError(message);
            toast.error(message);
        }
        setConfirmLoading(false);
    };

    const handleTemplateDownload = async () => {
        try {
            const ExcelJS = (await import("exceljs")).default;
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Intermediaires");
            sheet.columns = INTERMEDIAIRE_TEMPLATE_HEADERS.map((header) => ({
                header,
                key: header,
                width: Math.max(header.length + 4, 18),
            }));
            sheet.addRow(SAMPLE_ROW);
            sheet.getRow(1).font = { bold: true };
            sheet.getRow(2).font = { italic: true };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "template_import_intermediaires.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.log(error);
            toast.error("Impossible de télécharger le template");
        }
    };

    const renderImportStatus = (row) => {
        const hasErrors = Array.isArray(row?.errors) && row.errors.length > 0;
        const createdCode = row?.createdIntermediaireCodeId || row?.createdCodeId || row?.codeIntermediaire;
        if (importConfirm) {
            const created = !!createdCode && !hasErrors;
            return (
                <Tag color={created ? "green" : "red"} icon={created ? <CheckCircleFilled /> : <CloseCircleFilled />}>
                    {created ? "Créé" : "Erreur"}
                </Tag>
            );
        }
        const ok = row?.valid && !hasErrors;
        return (
            <Tag color={ok ? "green" : "red"} icon={ok ? <CheckCircleFilled /> : <CloseCircleFilled />}>
                {ok ? "Valide" : "Erreur"}
            </Tag>
        );
    };

    const renderBooleanTag = (value) => {
        const normalized = typeof value === "string" ? value.toLowerCase() : value;
        const isTrue = normalized === true || normalized === "true" || normalized === "1" || normalized === "oui";
        return (
            <Tag color={isTrue ? "blue" : "default"}>
                {isTrue ? "Actif" : "Inactif"}
            </Tag>
        );
    };

    const importColumns = [
        { title: "#", dataIndex: "rowNumber", key: "rowNumber", width: 70 },
        { title: "Code", dataIndex: "codeIntermediaire", key: "codeIntermediaire" },
        { title: "Nom", dataIndex: "nom", key: "nom" },
        { title: "Prénom", dataIndex: "prenom", key: "prenom" },
        { title: "Genre", dataIndex: "genre", key: "genre" },
        { title: "Contact", dataIndex: "contact", key: "contact" },
        { title: "Adresse", dataIndex: "adresse", key: "adresse" },
        { title: "Email", dataIndex: "email", key: "email" },
        { title: "Type personne", dataIndex: "typePersonne", key: "typePersonne" },
        { title: "Type intermédiaire", dataIndex: "typeIntermediaire", key: "typeIntermediaire" },
        {
            title: "Actif",
            dataIndex: "actif",
            key: "actif",
            render: (_, record) => renderBooleanTag(record?.actif ?? record?.active ?? record?.isActive),
        },
        { title: "Statut", key: "status", render: (_, record) => renderImportStatus(record) },
        {
            title: "Erreurs",
            dataIndex: "errors",
            key: "errors",
            render: (errors) =>
                errors && errors.length ? (
                    <Tooltip title={errors.join(", ")}>
                        <span>{errors.join(" | ")}</span>
                    </Tooltip>
                ) : (
                    <Tag color="green">Aucune</Tag>
                ),
        },
        {
            title: "Code créé",
            dataIndex: "createdIntermediaireCodeId",
            key: "createdIntermediaireCodeId",
            render: (_, record) =>
                record?.createdIntermediaireCodeId || record?.createdCodeId || record?.codeIntermediaire || "-",
        },
    ];

    const canConfirmImport = !!(importPreview && validRowsCount > 0 && importFile && !previewLoading);

    return (
        <Modal
            title="Import des intermédiaires (Excel)"
            open={open}
            onCancel={handleClose}
            width={1100}
            destroyOnClose
            footer={[
                <Button key="template" icon={<FileExcelOutlined />} onClick={handleTemplateDownload}>
                    Télécharger un template
                </Button>,
                <Button
                    key="preview"
                    type="primary"
                    onClick={handlePreviewImport}
                    disabled={!importFile}
                    loading={previewLoading}
                >
                    Prévisualiser
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    danger
                    onClick={handleConfirmImport}
                    disabled={!canConfirmImport}
                    loading={confirmLoading}
                >
                    Confirmer l'import
                </Button>,
            ]}
        >
            <Alert
                message={
                    <Space direction="vertical">
                        <span>En-têtes attendues : CodeIntermediaire, Nom, Prenom, Genre, Contact, Adresse, Email, TypePersonne, TypeIntermediaire, Actif.</span>
                        <span>Étape 1 : Prévisualisez pour voir les lignes valides/invalides. Étape 2 : Confirmez si au moins une ligne est valide.</span>
                    </Space>
                }
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
            />

            <Card size="small" style={{ marginBottom: 12, borderRadius: 10, background: "#fafafa" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Paragraph copyable code style={{ marginBottom: 4 }}>
                        {INTERMEDIAIRE_TEMPLATE_HEADERS.join(" | ")}
                    </Paragraph>
                    <Space wrap>
                        <Tag icon={<InfoCircleOutlined />} color={colors?.primary || "blue"}>
                            Genre : {GENRE_VALUES.join(" | ")}
                        </Tag>
                        <Tag color="geekblue">TypePersonne : {TYPE_PERSONNE_VALUES.join(" | ")}</Tag>
                        <Tag color="purple">TypeIntermediaire : {TYPE_INTERMEDIAIRE_VALUES.join(" | ")}</Tag>
                        <Tag color="gold">Actif : {ACTIF_VALUES.join(" / ")}</Tag>
                    </Space>
                </Space>
            </Card>

            <Card size="small" style={{ marginBottom: 12, borderRadius: 10 }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Text strong>Déposez votre fichier Excel</Text>
                    <Dragger
                        multiple={false}
                        accept=".xlsx,.xls"
                        beforeUpload={handleFileSelect}
                        showUploadList={false}
                        disabled={previewLoading || confirmLoading}
                        style={{ borderRadius: 12 }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Déposez le fichier ici ou cliquez pour sélectionner</p>
                        <p className="ant-upload-hint">Extensions acceptées : .xlsx, .xls</p>
                    </Dragger>
                    {importFile ? (
                        <Tag color="blue" icon={<UploadOutlined />}>
                            {importFile.name}
                        </Tag>
                    ) : null}
                </Space>
            </Card>

            {importError ? (
                <Alert style={{ marginBottom: 12 }} message={importError} type="error" showIcon />
            ) : null}

            {importSummary ? (
                <Space wrap style={{ marginBottom: 12 }}>
                    <Tag color="geekblue">Total : {importSummary.total}</Tag>
                    <Tag color="green">Valides : {importSummary.valid}</Tag>
                    <Tag color="red">Invalides : {importSummary.invalid}</Tag>
                    {confirmSummary ? (
                        <>
                            <Tag color="green">Créées : {confirmSummary.created}</Tag>
                            <Tag color="red">Échouées : {confirmSummary.failed}</Tag>
                        </>
                    ) : null}
                </Space>
            ) : null}

            {importSummary?.invalid > 0 ? (
                <Alert
                    style={{ marginBottom: 12 }}
                    message="Certaines lignes contiennent des erreurs. Corrigez-les ou continuez : seules les lignes valides seront créées."
                    type="warning"
                    showIcon
                />
            ) : null}

            {importPreview ? (
                <>
                    <Divider orientation="left">Prévisualisation</Divider>
                    <Table
                        columns={importColumns}
                        dataSource={importRows}
                        rowKey={(record) =>
                            record?.rowNumber || `${record.codeIntermediaire}-${record.nom}-${record.prenom}-${record.contact}`
                        }
                        pagination={{ pageSize: 8 }}
                        scroll={{ x: true }}
                    />
                </>
            ) : null}
        </Modal>
    );
};

export default ImportIntermediairesModal;
